import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const emptyAppraisal = {
  client_name: "", client_phone: "", client_email: "",
  vin: "", year: "", make: "", model: "", trim: "", color: "", body_type: "", fuel_type: "", odometer: "",
  accidents: "none", accident_details: "", owners: "", service_history: "unknown",
  tires_pct: "", brakes_pct: "", mechanical_notes: "", exterior_notes: "", interior_notes: "",
  options: "", notes: "",
};

const num = (v) => (v === "" || v == null ? null : Number(v));

export const appraisalToBody = (f) => ({
  ...f,
  year: Number(f.year || 0),
  odometer: Number(f.odometer || 0),
  owners: num(f.owners),
  tires_pct: num(f.tires_pct),
  brakes_pct: num(f.brakes_pct),
});

export const APPRAISAL_STEPS = [
  { key: "client", label: "Client" },
  { key: "vehicle", label: "Vehicle" },
  { key: "condition", label: "Condition" },
];

// `step` renders one section at a time (guided intake). Omit it to render the
// whole form on one page (used by the appraisal detail editor).
export default function AppraisalForm({ form, set, step }) {
  const show = (key) => step === undefined || APPRAISAL_STEPS[step]?.key === key;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-2">
      {show("client") && (
      <>
      <div className="col-span-full">
        <p className="label-mono">Client</p>
      </div>
      <div className="space-y-2"><Label className="label-mono">Client Name *</Label><Input value={form.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="appraisal-client-name" /></div>
      <div className="space-y-2"><Label className="label-mono">Phone</Label><Input value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} data-testid="appraisal-client-phone" /></div>
      <div className="space-y-2"><Label className="label-mono">Email</Label><Input type="email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} data-testid="appraisal-client-email" /></div>
      </>
      )}

      {show("vehicle") && (
      <>
      <div className="col-span-full border-t border-border pt-3 first:border-t-0 first:pt-0">
        <p className="label-mono">Vehicle</p>
      </div>
      <div className="col-span-2 space-y-2"><Label className="label-mono">VIN</Label><Input value={form.vin} onChange={(e) => set("vin", e.target.value)} className="font-mono" data-testid="appraisal-vin" /></div>
      <div className="space-y-2"><Label className="label-mono">Year *</Label><Input type="number" value={form.year} onChange={(e) => set("year", e.target.value)} data-testid="appraisal-year" /></div>
      <div className="space-y-2"><Label className="label-mono">Make *</Label><Input value={form.make} onChange={(e) => set("make", e.target.value)} data-testid="appraisal-make" /></div>
      <div className="space-y-2"><Label className="label-mono">Model *</Label><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="appraisal-model" /></div>
      <div className="space-y-2"><Label className="label-mono">Trim / Package</Label><Input value={form.trim} onChange={(e) => set("trim", e.target.value)} data-testid="appraisal-trim" /></div>
      <div className="space-y-2"><Label className="label-mono">Color</Label><Input value={form.color} onChange={(e) => set("color", e.target.value)} /></div>
      <div className="space-y-2"><Label className="label-mono">Fuel Type</Label>
        <Select value={form.fuel_type || undefined} onValueChange={(v) => set("fuel_type", v)}>
          <SelectTrigger data-testid="appraisal-fuel-select"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {["Gasoline", "Diesel", "Hybrid", "Plug-in Hybrid", "Electric", "Other"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2"><Label className="label-mono">Body Style</Label>
        <Select value={form.body_type || undefined} onValueChange={(v) => set("body_type", v)}>
          <SelectTrigger data-testid="appraisal-body-select"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {["Sedan", "Coupe", "Hatchback", "SUV", "Crossover", "Pickup Truck", "Minivan", "Cargo Van", "Passenger Van", "Wagon", "Convertible", "Sports Car", "Other"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2"><Label className="label-mono">Odometer (km) *</Label><Input type="number" value={form.odometer} onChange={(e) => set("odometer", e.target.value)} data-testid="appraisal-odometer" /></div>
      <div className="col-span-full space-y-2"><Label className="label-mono">Options / Packages</Label><Input value={form.options} onChange={(e) => set("options", e.target.value)} placeholder="M Sport, pano roof, nav, winter tire set…" data-testid="appraisal-options" /></div>
      </>
      )}

      {show("condition") && (
      <>
      <div className="col-span-full border-t border-border pt-3 first:border-t-0 first:pt-0">
        <p className="label-mono">Condition &amp; History</p>
      </div>
      <div className="space-y-2"><Label className="label-mono">Accidents</Label>
        <Select value={form.accidents} onValueChange={(v) => set("accidents", v)}>
          <SelectTrigger data-testid="appraisal-accidents"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None reported</SelectItem>
            <SelectItem value="minor">Minor (under $5k)</SelectItem>
            <SelectItem value="major">Major / structural</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 space-y-2"><Label className="label-mono">Accident Details</Label><Input value={form.accident_details} onChange={(e) => set("accident_details", e.target.value)} placeholder="e.g. rear bumper repaint 2023, $3,200 claim" /></div>
      <div className="space-y-2"><Label className="label-mono"># of Owners</Label><Input type="number" value={form.owners} onChange={(e) => set("owners", e.target.value)} /></div>
      <div className="space-y-2"><Label className="label-mono">Service History</Label>
        <Select value={form.service_history} onValueChange={(v) => set("service_history", v)}>
          <SelectTrigger data-testid="appraisal-service-history"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="full">Full records</SelectItem>
            <SelectItem value="partial">Partial records</SelectItem>
            <SelectItem value="none">No records</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2 grid grid-cols-2 gap-2">
        <div className="space-y-2"><Label className="label-mono">Tires %</Label><Input type="number" min="0" max="100" value={form.tires_pct} onChange={(e) => set("tires_pct", e.target.value)} /></div>
        <div className="space-y-2"><Label className="label-mono">Brakes %</Label><Input type="number" min="0" max="100" value={form.brakes_pct} onChange={(e) => set("brakes_pct", e.target.value)} /></div>
      </div>
      <div className="col-span-full grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2"><Label className="label-mono">Mechanical Notes</Label><Textarea rows={2} value={form.mechanical_notes} onChange={(e) => set("mechanical_notes", e.target.value)} placeholder="Engine light, leaks, noises…" /></div>
        <div className="space-y-2"><Label className="label-mono">Exterior Notes</Label><Textarea rows={2} value={form.exterior_notes} onChange={(e) => set("exterior_notes", e.target.value)} placeholder="Scratches, dents, rust…" /></div>
        <div className="space-y-2"><Label className="label-mono">Interior Notes</Label><Textarea rows={2} value={form.interior_notes} onChange={(e) => set("interior_notes", e.target.value)} placeholder="Seat wear, smells, electronics…" /></div>
      </div>
      <div className="col-span-full space-y-2"><Label className="label-mono">Other Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
      </>
      )}
    </div>
  );
}
